'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { sendClientRequest } from '@/utils/client-request'
import { Session } from '@/utils/types'

interface ProductFilters {
  category?: string | null
  brand?: string | null
  tag?: string | null
  sort?: string
  filter?: string | null
  search?: string | null
}

interface UseInfiniteProductsProps {
  vendorId: string
  limit?: number
  initialFilters?: ProductFilters
  session?: Session | null
}

interface ProductsMetadata {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export function useInfiniteProducts({ vendorId, limit = 20, initialFilters = {}, session }: UseInfiniteProductsProps) {
  const [products, setProducts] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<ProductsMetadata>({ total: 0, limit, offset: 0, hasMore: true })
  const [filters, setFilters] = useState<ProductFilters>(initialFilters)

  const offsetRef = useRef(0)
  const hasMoreRef = useRef(true)
  const isLoadingRef = useRef(false)

  const buildQueryParams = useCallback((offset: number, currentFilters: ProductFilters) => {
    const params = new URLSearchParams()
    params.set('limit', limit.toString())
    params.set('offset', offset.toString())

    if (currentFilters.category) params.set('category', currentFilters.category)
    if (currentFilters.brand) params.set('brand', currentFilters.brand)
    if (currentFilters.tag) params.set('tag', currentFilters.tag)
    if (currentFilters.sort) params.set('sort', currentFilters.sort)
    if (currentFilters.filter) params.set('filter', currentFilters.filter)
    if (currentFilters.search) params.set('search', currentFilters.search)

    return params.toString()
  }, [limit])

  const fetchProducts = useCallback(async (offset: number, currentFilters: ProductFilters, isInitial: boolean = false) => {
    if (isLoadingRef.current) return
    if (!isInitial && !hasMoreRef.current) return

    isLoadingRef.current = true
    if (isInitial) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }
    setError(null)

    try {
      const queryString = buildQueryParams(offset, currentFilters)
      const result = await sendClientRequest({ url: `/products/paginated?${queryString}`, method: 'GET', auth: !!(session as any)?.user?.id, session: session || undefined, headers: { Vendor: vendorId } })

      if (!result || result.status !== 200) {
        setError(result?.error || 'Failed to fetch products')
        return
      }

      const newProducts = result.data || []
      const newMetadata = result.metadata || { total: 0, limit, offset: 0, hasMore: false }

      let addedCount = newProducts.length
      if (isInitial) {
        setProducts(newProducts)
      } else {
        setProducts(prev => {
          const existingIds = new Set(prev.map((p: any) => p.id))
          const uniqueNew = newProducts.filter((p: any) => !existingIds.has(p.id))
          addedCount = uniqueNew.length
          return [...prev, ...uniqueNew]
        })
      }

      const stalled = !isInitial && newProducts.length > 0 && addedCount === 0
      setMetadata(stalled ? { ...newMetadata, hasMore: false } : newMetadata)
      offsetRef.current = offset + newProducts.length
      hasMoreRef.current = newMetadata.hasMore && newProducts.length > 0 && !stalled
    } catch (err) {
      setError('Failed to fetch products')
      console.error('Error fetching products:', err)
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [vendorId, limit, buildQueryParams, session])

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || isLoadingRef.current) return
    fetchProducts(offsetRef.current, filters, false)
  }, [fetchProducts, filters])

  const reset = useCallback((newFilters?: ProductFilters) => {
    const filtersToUse = newFilters || filters
    setProducts([])
    offsetRef.current = 0
    hasMoreRef.current = true
    setMetadata({ total: 0, limit, offset: 0, hasMore: true })
    if (newFilters) {
      setFilters(newFilters)
    }
    fetchProducts(0, filtersToUse, true)
  }, [filters, limit, fetchProducts])

  const updateFilters = useCallback((newFilters: Partial<ProductFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)
    setProducts([])
    offsetRef.current = 0
    hasMoreRef.current = true
    setMetadata({ total: 0, limit, offset: 0, hasMore: true })
    fetchProducts(0, updatedFilters, true)
  }, [filters, limit, fetchProducts])

  useEffect(() => {
    fetchProducts(0, initialFilters, true)
  }, [])

  return {
    products,
    isLoading,
    isLoadingMore,
    error,
    metadata,
    // ponytail: products known to exist but not fetched yet. Grids render a placeholder
    // per pending product so the page is its final height on first paint — no scrollbar
    // jump when a batch lands. Capped so a huge catalog can't blow up the DOM; if a
    // vendor ever hits the cap, virtualise the grid instead of raising it.
    pendingCount: Math.min(500, Math.max(0, metadata.total - products.length)),
    hasMore: metadata.hasMore,
    loadMore,
    reset,
    updateFilters,
    filters
  }
}
